/**
 * @file PutStockDialog.tsx
 * @description Диалог «Положить товар» в ячейку (phase19.23, A4).
 *
 * Два шага: найти фасовку с остатком на складе ячейки → выбрать партию или
 * «все партии». Размещение = перестановка партии (`move-bin`), остаток и склад
 * не меняются.
 *
 * На 360px диалог полноэкранный со sticky-кнопкой внизу: кладовщик работает
 * одной рукой, кнопка обязана быть под пальцем.
 */

import { ChevronLeft, Loader2, PackagePlus, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { PutStockLotList } from "./PutStockLotList";
import { usePutStock, type PutStockSku } from "./usePutStock";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { EmptyState } from "../../ui/EmptyState";
import { SearchInput } from "../../ui/SearchInput";
import type { StorageBinDto } from "../../../lib/api-bins";

interface Props {
  bin: StorageBinDto;
  /** Партии размещены — обновить список ячеек и содержимое. */
  onPlaced: () => void;
  onClose: () => void;
}

export function PutStockDialog({ bin, onPlaced, onClose }: Props) {
  const stock = usePutStock(bin.warehouseId, bin.id, bin.code);
  const [search, setSearch] = useState("");
  const [sku, setSku] = useState<PutStockSku | null>(null);
  const [selected, setSelected] = useState<"all" | string[]>("all");

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stock.skus;
    return stock.skus.filter(
      (s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q),
    );
  }, [stock.skus, search]);

  /** Ничего не выбрано — класть нечего, кнопка заблокирована. */
  const nothingSelected = selected !== "all" && selected.length === 0;

  async function handlePlace() {
    if (!sku) return;
    const lots =
      selected === "all"
        ? sku.lots
        : sku.lots.filter((lot) => selected.includes(lot.lotId));
    const ok = await stock.place(lots);
    // Обновляем сводку и шторку всегда: при частичном сбое часть партий уже
    // переехала, а экран позади диалога об этом ещё не знает.
    onPlaced();
    if (ok) onClose();
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* sm:max-h-80vh — список фасовок скроллится внутри диалога, не страницей. */}
      <DialogContent fullScreen className="sm:max-h-[80vh] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {sku ? `Партии: ${sku.name}` : `Положить товар в ${bin.code}`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          {stock.error ? <Alert severity="error">{stock.error}</Alert> : null}

          {sku ? (
            <>
              <button
                type="button"
                onClick={() => setSku(null)}
                className="flex min-h-[56px] items-center gap-1 self-start text-sm text-primary"
              >
                <ChevronLeft className="size-4" />К выбору фасовки
              </button>
              <PutStockLotList
                sku={sku}
                targetBinId={bin.id}
                targetBinCode={bin.code}
                selected={selected}
                onSelect={setSelected}
              />
            </>
          ) : (
            <>
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Название или артикул"
                ariaLabel="Поиск фасовки на складе"
                size="wms"
              />
              {stock.loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="size-5 animate-spin text-primary" />
                </div>
              ) : visible.length === 0 ? (
                <EmptyState
                  icon={Search}
                  variant={search ? "filtered" : "empty"}
                  title={
                    search
                      ? "По этому запросу остатков нет"
                      : "На складе нет партий с остатком"
                  }
                  description={
                    search
                      ? "Проверьте артикул или сбросьте поиск."
                      : "Сначала проведите приёмку — класть в ячейку пока нечего."
                  }
                />
              ) : (
                <ul className="flex flex-col gap-2">
                  {visible.map((row) => (
                    <li key={row.skuId}>
                      <button
                        type="button"
                        onClick={() => {
                          setSku(row);
                          setSelected("all");
                        }}
                        className="flex min-h-[56px] w-full flex-col justify-center rounded-xl border border-on-surface/15 px-3 py-2 text-left"
                      >
                        <span className="truncate text-sm font-medium">{row.name}</span>
                        <span className="truncate font-mono text-xs text-on-surface-variant">
                          {row.code} · {row.totalQuantity.toLocaleString("ru-RU")} шт ·
                          партий {row.lots.length}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        {/* На 360px кнопка прилипает к низу: диалог полноэкранный. */}
        <DialogFooter className="max-sm:sticky max-sm:bottom-0 max-sm:bg-surface-lowest max-sm:pt-2">
          <Button variant="ghost" className="min-h-[56px]" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="min-h-[56px]"
            disabled={!sku || nothingSelected || stock.saving}
            onClick={() => void handlePlace()}
          >
            <PackagePlus className="size-5" />
            <span className="ml-1.5">
              {stock.saving ? "Кладём…" : `Положить в ${bin.code}`}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
